// ActionsToolbar.tsx
import { CopyIcon, PlayIcon, SaveIcon } from 'lucide-react';

import { Button } from '@/components/Button';
import { UserSelectorDropdown } from '@/sections/insights/queryBuilder/components/QueryDisplay/UserSelectorDropdown';

interface ActionsToolbarProps {
  editable: boolean;
  isGlobal: boolean;
  onExecute: () => void;
  onDuplicate: () => void;
  onSave?: () => void;
  isSaving?: boolean;
}

/**
 * ActionsToolbar - Floating action panel for QuerySplitVisualizer
 *
 * Based on QueryViewer/QueryEditor action panels
 * Supports both view-only and edit modes
 */
export const ActionsToolbar = ({
  editable,
  isGlobal,
  onExecute,
  onDuplicate,
  onSave,
  isSaving = false,
}: ActionsToolbarProps) => {
  return (
    <div
      className="border-chariot-stroke bg-chariot-background-primary absolute top-16 right-8 z-10 flex items-center gap-2 rounded-md border px-3 py-2"
      data-testid="actions-toolbar"
      role="group"
      aria-label="Query visualizer actions"
    >
      {/* Execute - Primary action */}
      <Button
        styleType="rounded"
        size="xxs"
        onClick={onExecute}
        label="Execute"
        startIcon={<PlayIcon className="size-4" />}
        className="bg-chariot-brand-primary hover:bg-chariot-brand-secondary text-white"
        data-testid="actions-toolbar-execute-btn"
        aria-label="Execute query"
      />

      {/* Separator */}
      <div
        className="bg-chariot-stroke h-6 w-px"
        role="separator"
        aria-orientation="vertical"
      />

      {/* User Selector (conditional: global mode) */}
      {isGlobal && (
        <>
          <UserSelectorDropdown />
          <div
            className="bg-chariot-stroke h-6 w-px"
            role="separator"
            aria-orientation="vertical"
          />
        </>
      )}

      {/* Save (conditional: editable mode) */}
      {editable && onSave && (
        <>
          <Button
            styleType="rounded"
            size="xxs"
            onClick={onSave}
            label={isSaving ? 'Saving...' : 'Save'}
            startIcon={<SaveIcon className="size-4" />}
            className="bg-chariot-brand-primary hover:bg-chariot-brand-secondary text-white"
            data-testid="actions-toolbar-save-btn"
            aria-label="Save query"
            disabled={isSaving}
          />
          <div
            className="bg-chariot-stroke h-6 w-px"
            role="separator"
            aria-orientation="vertical"
          />
        </>
      )}

      {/* Duplicate - Secondary action */}
      <Button
        styleType="rounded"
        size="xxs"
        onClick={onDuplicate}
        label="Duplicate"
        startIcon={<CopyIcon className="size-4" />}
        data-testid="actions-toolbar-duplicate-btn"
        aria-label="Duplicate query"
      />
    </div>
  );
};
